import { useState } from "react";
import { api } from "@/lib/api";
import { Clock, Loader2, Wrench } from "lucide-react";
import { toast } from "sonner";

// Compact scheduler editor — sits on the customer sheet so the advisor sets
// (a) what work is on this booking, and (b) what time the customer wants it
// ready. The backend derives duration + latest-start-time from these two so
// the Workshop Control schedule can prioritise properly.
//
// Work-type ids match `job_scheduler.WORK_TYPE_DURATION_MINUTES` in the
// backend — keep them in sync or the duration falls back to a 60-min guess.
const WORK_TYPES = [
  { id: "small_service",  label: "Small service",  mins: 60  },
  { id: "large_service",  label: "Large service",  mins: 75  },
  { id: "mot",            label: "MOT",            mins: 60  },
  { id: "brakes",         label: "Brakes",         mins: 60  },
  { id: "diagnostic",     label: "Diagnostic",     mins: 90  },
  { id: "tyres",          label: "Tyres",          mins: 30  },
  { id: "aircon",         label: "A/C service",    mins: 30  },
  { id: "wheel_alignment",label: "Wheel alignment",mins: 48  },
  { id: "cambelt",        label: "Cambelt",        mins: 240 },
  { id: "clutch",         label: "Clutch",         mins: 300 },
  { id: "recall",         label: "OEM recall",     mins: 30  },
];

export default function JobScheduleEditor({ job, onChanged }) {
  const [workTypes, setWorkTypes] = useState(job.work_types || []);
  const [dropoff, setDropoff] = useState(job.appointment_time || "");
  const [promised, setPromised] = useState(job.promised_ready_time || "");
  const [saving, setSaving] = useState(false);

  const toggle = (id) => setWorkTypes((prev) => prev.includes(id) ? prev.filter((t) => t !== id) : [...prev, id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/jobs/${job.id}`, {
        work_types: workTypes,
        appointment_time: dropoff || null,
        promised_ready_time: promised || null,
      });
      toast.success("Schedule updated.");
      onChanged?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save the schedule.");
    } finally {
      setSaving(false);
    }
  };

  const totalMins = workTypes.reduce((n, id) => {
    const w = WORK_TYPES.find((x) => x.id === id);
    return n + (w ? w.mins : 0);
  }, 0);
  const capped = Math.min(totalMins, 240);
  const hh = Math.floor(capped / 60);
  const mm = capped % 60;

  return (
    <div className="card-default p-5" data-testid="job-schedule-editor">
      <div className="flex items-start gap-3">
        <div className="w-10 h-10 rounded-lg bg-primary/15 text-primary grid place-items-center shrink-0">
          <Wrench className="w-5 h-5" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="label-overline text-primary">Workshop schedule</div>
          <div className="font-display font-bold text-base">
            What's on this booking?
          </div>
          <p className="text-xs text-muted-foreground mt-1">
            Ticks the work + a promised-ready time drives your priority list on Workshop Control.
          </p>
        </div>
      </div>

      <div className="mt-3 flex flex-wrap gap-1.5">
        {WORK_TYPES.map((w) => (
          <button
            key={w.id}
            type="button"
            onClick={() => toggle(w.id)}
            className={`text-xs px-3 py-1.5 rounded-full border transition-colors ${
              workTypes.includes(w.id)
                ? "bg-primary text-primary-foreground border-primary"
                : "bg-background/40 text-muted-foreground border-border hover:bg-muted"
            }`}
            data-testid={`sched-worktype-${w.id}`}
          >
            {w.label}
            <span className="opacity-70 ml-1">· {w.mins}m</span>
          </button>
        ))}
      </div>

      <div className="mt-4 grid grid-cols-2 sm:grid-cols-[1fr_1fr_auto_auto] items-end gap-2">
        <label className="text-xs">
          <span className="label-overline block mb-1">Drop-off time</span>
          <div className="relative">
            <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="time"
              value={dropoff}
              onChange={(e) => setDropoff(e.target.value)}
              className="input-default text-sm pl-9 w-full h-10"
              data-testid="sched-dropoff-time"
              title="Overrides whatever the diary imported"
            />
          </div>
        </label>
        <label className="text-xs">
          <span className="label-overline block mb-1">Promised ready time</span>
          <div className="relative">
            <Clock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <input
              type="time"
              value={promised}
              onChange={(e) => setPromised(e.target.value)}
              className="input-default text-sm pl-9 w-full h-10"
              data-testid="sched-promised-time"
              title="Overrides whatever the diary imported"
            />
          </div>
        </label>
        <div className="text-xs">
          <div className="label-overline mb-1">Duration</div>
          <div className="font-mono-plex text-sm font-bold h-10 flex items-center px-3 rounded-md border border-border bg-background/40" data-testid="sched-duration">
            {totalMins ? (hh ? `${hh}h ${mm ? mm + "m" : ""}`.trim() : `${mm}m`) : "—"}
          </div>
        </div>
        <button
          type="button"
          onClick={save}
          disabled={saving}
          className="btn-primary text-sm inline-flex items-center gap-1.5 h-10"
          data-testid="sched-save"
        >
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : null}
          Save
        </button>
      </div>

      {job.latest_start_time && (
        <div className="text-[11px] text-muted-foreground mt-3 space-y-1" data-testid="sched-latest-hint">
          <div>
            To hit {job.promised_ready_time}, this needs to be on a lift by{" "}
            <span className="font-bold text-foreground font-mono-plex">
              {new Date(job.latest_start_time).toLocaleTimeString("en-GB", { hour: "2-digit", minute: "2-digit" })}
            </span>.
          </div>
          {job.schedule_breakdown && (
            <div data-testid="sched-breakdown">
              Includes{" "}
              <span className="font-bold text-foreground">{job.schedule_breakdown.work_mins}m work</span>
              {job.schedule_breakdown.wash_mins > 0 && <> · <span className="font-bold text-foreground">{job.schedule_breakdown.wash_mins}m wash</span></>}
              {" "}· <span className="font-bold text-foreground">{job.schedule_breakdown.paperwork_mins}m paperwork/QC</span>
              {job.schedule_breakdown.wash_mins === 0 && <em className="ml-1">(wait-in — no wash)</em>}
            </div>
          )}
        </div>
      )}
    </div>
  );
}
